import { Box } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UI } from "../components/ActionDrawer";
import DashTable, {
  Notice,
  Oid,
  PageHead,
  Sub2,
  Tag,
} from "../components/DashTable";
import PageLoader from "../components/Loader";
import { useToast } from "../components/Toast";
import apiService from "../Services/api.service";

/* What has crossed into Zoho Books, and what has not. */
const syncTag = (state) => {
  if (state === "synced") return <Tag tone="g">Synced</Tag>;
  if (state === "failed") return <Tag tone="b">Failed</Tag>;
  if (state === "not_raised") return <Tag tone="b">Not raised</Tag>;
  return <Tag tone="i">n/a</Tag>;
};

const ZohoSync = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const token = localStorage.getItem("token");

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const fetchRows = async () => {
    setIsLoading(true);
    setLoadFailed(false);
    try {
      const res = await apiService.fetchZohoSync(token);
      setRows(res.data || []);
      setSummary(res.summary || null);
    } catch (error) {
      setLoadFailed(true);
      toast.error("Error while fetching Zoho sync status");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    { label: "Order" },
    { label: "Clinic" },
    { label: "Contact" },
    { label: "Quotation" },
    { label: "Receipts" },
    { label: "Invoice" },
    { label: "Failures" },
  ];

  const tableRows = rows.map((row) => ({
    id: row.orderId,
    cells: [
      <>
        <Oid>{row.orderNumber}</Oid>
        <Sub2>{row.doctorName}</Sub2>
      </>,
      row.clinicName,
      syncTag(row.contactSynced),
      syncTag(row.quotationSynced),
      syncTag(row.receiptsSynced),
      syncTag(row.invoiceSynced),
      row.failures ? (
        <Box component="span" sx={{ color: UI.red, fontWeight: 500 }}>
          {row.failures} {row.failures === 1 ? "retry" : "retries"}
        </Box>
      ) : (
        "—"
      ),
    ],
  }));

  const kpis = summary
    ? [
        { label: "Orders tracked", value: summary.tracked ?? rows.length },
        { label: "Retries in 24h", value: summary.retries24h, alarm: true },
        { label: "Invoices missing", value: summary.invoicesMissing },
        { label: "Fully synced", value: summary.fullySynced },
      ]
    : [];

  return (
    <Box sx={{ padding: { xs: "18px 16px 50px", md: "24px 28px 60px" }, maxWidth: 1240 }}>
      <PageHead
        title="Zoho Books sync"
        lede="What has crossed into Books, and what has not."
        kpis={kpis}
      />

      <Notice>
        <b>Sync happens at four points, not one.</b> Contact on order creation,
        quotation on generation, receipts on payment, invoice on dispatch. The
        flow shows a single Zoho box at the end, which understates the
        integration.
      </Notice>

      <DashTable
        columns={columns}
        rows={tableRows}
        onRowClick={(id) => navigate(`/dashboard/orders/${id}`)}
        emptyText="Nothing tracked yet."
        errorText={loadFailed ? "Could not load Zoho sync status." : null}
        onRetry={fetchRows}
        foot={
          summary
            ? `${summary.tracked ?? rows.length} orders tracked · ${
                summary.retries24h ?? 0
              } retries in the last 24 hours`
            : null
        }
      />

      {isLoading && <PageLoader />}
    </Box>
  );
};

export default ZohoSync;
